import { Box, Checkbox, Tooltip } from '@mui/material';
import { CompanyRole } from 'constants/CompanyRole';
import { getAvailabilityIntervals } from 'modules/availabilities/company-availability/helpers';
import { CompanyAvailabilityRegularCellProps } from 'modules/availabilities/company-availability/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { profileSelector } from 'store/selectors/profile-selectors';
import { AVAILABILITIES__NO_AVAILABILITIES, AVAILABILITIES__UNAVAILABLE } from 'translations/constants';
import { ReactComponent as AddNewContractIcon } from '../../../../../../assets/icons/CircleAddNewContractIcon.svg';
import ContractHoursHoverCard from './ContractHoursHoverCard';
import {
  AvailabilityBarBody,
  AvailabilityBarContainer,
  AvailabilityBarSegment,
  AvailabilityCellActions,
  AvailabilityCellOptions,
  HasContractDot,
  RegularCellBody,
  RegularCellContainer,
  RegularCellTextContainer,
} from './styles';

const CompanyAvailabilityRegularCell = (props: CompanyAvailabilityRegularCellProps) => {
  const {
    availabilityDay,
    companyAvailabilityIntervals,
    canBeSelected,
    onSelectDay,
    isChecked,
    onAddContractForDay,
    buttonDisplayed,
    month,
    hasDaysInPreviousMonth,
    areAllCardsUnhovered,
  } = props;
  const [t] = useTranslation();

  const role = useAppSelector(profileSelector)?.role;
  const canViewContractIndicator =
    role === CompanyRole.ADMIN ||
    [CompanyRole.COMPANY_MANAGER, CompanyRole.HR_MANAGER, CompanyRole.LOCATION_MANAGER].includes(role);

  const contractCount = availabilityDay.contractCount ?? 0;
  const contractHours = availabilityDay.contractHours ?? [];
  const showContractIndicator = canViewContractIndicator && contractCount > 0;

  const [availabilityIntervals, setAvailabilityIntervals] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const displayAvailabilityIntervals = () => {
    const intervals = getAvailabilityIntervals(availabilityDay, companyAvailabilityIntervals);
    if (intervals[0]?.startsWith('[AVAILABILITIES]')) {
      setAvailabilityIntervals([t(intervals[0])]);
      return;
    }
    setAvailabilityIntervals(intervals);
  };

  useEffect(displayAvailabilityIntervals, [availabilityDay, companyAvailabilityIntervals, t]);

  const isUnavailable = availabilityIntervals[0] === t(AVAILABILITIES__UNAVAILABLE);
  const isNotSubmitted = availabilityIntervals[0] === t(AVAILABILITIES__NO_AVAILABILITIES);
  const isAvailable = !isUnavailable && !isNotSubmitted;

  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();
  const isInThePast =
    currentMonth > month ||
    (currentMonth === month && currentDay > availabilityDay.day) || // days in the same month but in the past
    (currentMonth === month - 1 &&
      hasDaysInPreviousMonth &&
      currentDay > availabilityDay.day &&
      availabilityDay.day > 7) || // days from the current month but in the past, at the end of the month
    (currentMonth === month && hasDaysInPreviousMonth && availabilityDay.day > 7); // days from the previous month

  useEffect(() => {
    if (areAllCardsUnhovered && isHovered) {
      setIsHovered(false);
    }
  }, [areAllCardsUnhovered, isHovered]);

  return (
    // The hover panel is triggered from the whole day cell (not the count badge) because the hover
    // overlay with the "add contract" icon covers the badge on selectable/future days.
    <Tooltip
      placement="top"
      disableHoverListener={!showContractIndicator}
      title={showContractIndicator ? <ContractHoursHoverCard contractHours={contractHours} /> : ''}
      componentsProps={{
        tooltip: { sx: { backgroundColor: 'transparent', padding: 0, margin: 0, maxWidth: 'none' } },
      }}
    >
      <RegularCellContainer
        onMouseEnter={() => canBeSelected && !isInThePast && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {(isHovered || isChecked) && (
          <AvailabilityCellOptions>
            <Checkbox
              checked={isChecked}
              onChange={() => onSelectDay(availabilityDay.day)}
              sx={{ padding: '0px', width: '16px', height: '16px', position: 'absolute', top: '8px', right: '8px' }}
              disabled={!canBeSelected}
            />
            {buttonDisplayed && (
              <AvailabilityCellActions>
                <Box sx={{ cursor: 'pointer' }}>
                  <AddNewContractIcon
                    onClick={() => {
                      onAddContractForDay(availabilityDay.day);
                    }}
                  />
                </Box>
              </AvailabilityCellActions>
            )}
          </AvailabilityCellOptions>
        )}
        <RegularCellBody isHovered={isHovered}>
          <AvailabilityBarContainer>
            <AvailabilityBarBody
              isAvailable={isAvailable}
              isNotSubmitted={isNotSubmitted}
              hasContract={showContractIndicator}
            >
              <AvailabilityBarSegment isAvailable={availabilityDay.isAvailableMorning} />
              <AvailabilityBarSegment isAvailable={availabilityDay.isAvailableAfternoon} />
              <AvailabilityBarSegment isAvailable={availabilityDay.isAvailableEvening} />
              {showContractIndicator && <HasContractDot>{contractCount}</HasContractDot>}
            </AvailabilityBarBody>
          </AvailabilityBarContainer>
          <RegularCellTextContainer isAvailable={isAvailable}>{availabilityIntervals[0]}</RegularCellTextContainer>
          <RegularCellTextContainer isAvailable>{availabilityIntervals[1]}</RegularCellTextContainer>
        </RegularCellBody>
      </RegularCellContainer>
    </Tooltip>
  );
};

export default CompanyAvailabilityRegularCell;
